import { IEmployee } from '../../settings/types/employee'
import { IProjectCurrentTasks, IProjectCurrentDirectCosts } from '../../project/types/project'
import { IProjectActivity } from '../../project/types/projectActivity'
import { TimeTrackingApi } from '../../../api/timeTrackingApi'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { ActionCreator } from 'redux'
import { toastMsg } from '../../../components/Toast/toastMsg'
import { TimeTrackingState } from '../reducers'
import { ITimeTracking, ITimeTrackingChart, ITimeTrackingDonutChart, ITimeTrackingInvolvedTaskFilter, ITimeTrackingInvolvedTasks } from '../types/timeTracking'
import * as constants from './constants'
import { IPagination } from '../../../types/pagination'
import { IDirectCost } from '../../project/types/directAndPassTroughCosts'
import { AxiosResponse } from 'axios'

export type TimeTrackingThunkResult<R> = ThunkAction<Promise<R>, TimeTrackingState, null, TimeTrackingActions>
export type TimeTrackingThunkDispatchType = ThunkDispatch<TimeTrackingState, null, TimeTrackingActions>

export interface TimeTrackingHasErrored {
  type: constants.TIME_TRACKING_HAS_ERRORED
  hasErrored: boolean
}

export interface SetActiveTimetrackingTab {
  type: constants.SET_ACTIVE_TIMETRACKING_TAB
  tab: string
}

export interface GetTimeTrackingEntriesSuccess {
  type: constants.GET_TIME_TRACKING_ENTRIES_SUCCESS
  timeTrackingEntries: ITimeTracking[]
}

export interface UpdateTimeTrackingEntrySuccess {
  type: constants.UPDATE_TIME_TRACKING_ENTRY_SUCCESS
  timeTrackingEntry: ITimeTracking
}

export interface CreateTimeTrackingEntrySuccess {
  type: constants.CREATE_TIME_TRACKING_ENTRY_SUCCESS
  timeTrackingEntry: ITimeTracking
}

export interface DeleteTimeTrackingEntrySuccess {
  type: constants.DELETE_TIME_TRACKING_ENTRY_SUCCESS
  timeTrackingEntry: ITimeTracking
}

export interface GetTimeTrackingInvolvedTasksSuccess {
  type: constants.GET_TIME_TRACKING_INVOLVED_TASKS_SUCCESS
  timeTrackingInvolvedTasks: ITimeTrackingInvolvedTasks[]
}

export interface GetTimeTrackingInvolvedTasksHasErrored {
  type: constants.GET_TIME_TRACKING_INVOLVED_TASKS_HAS_ERRORED
  hasErrored: boolean
}

export interface FilterTimeTrackingInvolvedTasks {
  type: constants.FILTER_TIME_TRACKING_INVOLVED_TASKS,
  filters: ITimeTrackingInvolvedTaskFilter
}

export interface GetTimeTrackingChartDataSuccess {
  type: constants.GET_TIME_TRACKING_CHART_DATA_SUCCESS
  timeTrackingChart: ITimeTrackingChart
}

export interface GetTimeTrackingChartDataDonutSuccess {
  type: constants.GET_TIME_TRACKING_CHART_DATA_DONUT_SUCCESS
  timeTrackingDonutChart: ITimeTrackingDonutChart
}

export interface GetCurrentTasksSuccess {
  type: constants.GET_CURRENT_TASKS_SUCCESS
  data: IProjectCurrentTasks
}

export interface GetPaginatedCurrentTasksSuccess {
  type: constants.GET_PAGINATED_CURRENT_TASKS_SUCCESS
  data: IPagination<IProjectActivity[]>
}

export interface GetCurrentDirectCostsSuccess {
  type: constants.GET_CURRENT_DIRECT_COSTS_SUCCESS
  data: IProjectCurrentDirectCosts
}

export interface GetPaginatedCurrentDirectCostsSuccess {
  type: constants.GET_PAGINATED_CURRENT_DIRECT_COSTS_SUCCESS
  data: IPagination<IDirectCost[]>
}

export interface SetPaginatedCurrentTasksActivePage {
  type: constants.SET_PAGINATED_CURRENT_TASKS_ACTIVE_PAGE
  page: number
}

export interface SetCurrentTasksStatusFilter {
  type: constants.SET_CURRENT_TASKS_STATUS_FILTER
  status: 'in_progress' | 'completed'
}

export interface GetAdminTimeTrackingEntriesSuccess {
  type: constants.GET_ADMIN_TIME_TRACKING_ENTRIES_SUCCESS
  timeTrackingEntries: ITimeTracking[]
}

export interface UpdateAdminTimeTrackingEntrySuccess {
  type: constants.UPDATE_ADMIN_TIME_TRACKING_ENTRY_SUCCESS
  timeTrackingEntry: ITimeTracking
}

export interface CreateAdminTimeTrackingEntrySuccess {
  type: constants.CREATE_ADMIN_TIME_TRACKING_ENTRY_SUCCESS
  timeTrackingEntry: ITimeTracking
}

export interface SelectEmployeeForTimeTracking {
  type: constants.SELECT_EMPLOYEE_FOR_TIME_TRACKING
  employee: IEmployee | null
}

export interface GetPaginatedProjectTimeTrackingInvolvedTasksSuccess {
  type: constants.GET_PAGINATED_TIME_TRACKING_INVOLVED_TASKS_SUCCESS
  data: IPagination<ITimeTrackingInvolvedTasks[]>
}

export interface SetPaginatedProjectTimeTrackingInvolvedTasksActivePage {
  type: constants.SET_PAGINATED_TIME_TRACKING_INVOLVED_TASKS_ACTIVE_PAGE
  page: number
}

export interface SearchProjectTimeTrackingInvolvedTasks {
  type: constants.SEARCH_PROJECT_TIME_TRACKING_INVOLVED_TASKS
  queryParams: Record<string, string>
}

export interface SortProjectTimeTrackingInvolvedTasksReport {
  type: constants.SORT_PROJECT_TIME_TRACKING_INVOLVED_TASKS_REPORT
  sort: { name: string, direction: string }
}

export const timeTrackingHasErrored = (hasErrored: boolean, error: Error, action?: string): TimeTrackingHasErrored => {
  if (action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.TIME_TRACKING_HAS_ERRORED,
    hasErrored: hasErrored
  }
}

export const setActiveTimetrackingTab = (tab: string): SetActiveTimetrackingTab => {
  return {
    type: constants.SET_ACTIVE_TIMETRACKING_TAB,
    tab
  }
}

export const getTimeTrackingEntries: ActionCreator<TimeTrackingThunkResult<ITimeTracking[]>>
  = (queryParams?: Record<string, string>): TimeTrackingThunkResult<ITimeTracking[]> => {
  return async (dispatch: TimeTrackingThunkDispatchType) => {
    return TimeTrackingApi.getTimeTrackingEntries(queryParams)
      .then(response => {
        dispatch(getTimeTrackingSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(timeTrackingHasErrored(true, error.response.data, constants.GET_TIME_TRACKING_ENTRIES))
        return []
      })
  }
}

export const getTimeTrackingSuccess = (timeTrackingEntries: ITimeTracking[]): GetTimeTrackingEntriesSuccess => {
  return {
    type: constants.GET_TIME_TRACKING_ENTRIES_SUCCESS,
    timeTrackingEntries
  }
}

export const updateTimeTracking: ActionCreator<TimeTrackingThunkResult<ITimeTracking>>
  = (entry: ITimeTracking): TimeTrackingThunkResult<ITimeTracking>  => {
  return async (dispatch: TimeTrackingThunkDispatchType) => {
    return TimeTrackingApi.updateTimeTrackingEntry(entry)
      .then(response => {
        dispatch(updateTimeTrackingSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(timeTrackingHasErrored(true, error.response.data, constants.UPDATE_TIME_TRACKING_ENTRY))
        return {} as ITimeTracking
      })
  }
}

export const updateTimeTrackingSuccess = (timeTrackingEntry: ITimeTracking): UpdateTimeTrackingEntrySuccess => {
  toastMsg(`Updated tracked time for ${timeTrackingEntry.activity ? timeTrackingEntry.activity.name : timeTrackingEntry.direct_cost.offer_position} successfully.`, constants.UPDATE_TIME_TRACKING_ENTRY_SUCCESS, 'success')
  return {
    type: constants.UPDATE_TIME_TRACKING_ENTRY_SUCCESS,
    timeTrackingEntry
  }
}

export const createTimeTracking: ActionCreator<TimeTrackingThunkResult<ITimeTracking>>
  = (entry: ITimeTracking): TimeTrackingThunkResult<ITimeTracking>  => {
  return async (dispatch: TimeTrackingThunkDispatchType) => {
    return TimeTrackingApi.createTimeTrackingEntry(entry)
      .then(response => {
        dispatch(createTimeTrackingSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(timeTrackingHasErrored(true, error.response.data, constants.CREATE_TIME_TRACKING_ENTRY))
        return {} as ITimeTracking
      })
    }
  }

  export const createTimeTrackingSuccess = (timeTrackingEntry: ITimeTracking): CreateTimeTrackingEntrySuccess => {
    toastMsg(`Created tracked time entry successfully.`, constants.CREATE_TIME_TRACKING_ENTRY_SUCCESS, 'success')
    return {
      type: constants.CREATE_TIME_TRACKING_ENTRY_SUCCESS,
      timeTrackingEntry
    }
  }

/* ADMIN */
export const getAdminTimeTrackingEntries: ActionCreator<TimeTrackingThunkResult<ITimeTracking[]>>
  = (queryParams: Record<string, string>): TimeTrackingThunkResult<ITimeTracking[]> => {
  return async (dispatch: TimeTrackingThunkDispatchType) => {
    return TimeTrackingApi.getAdminTimeTrackingEntries(queryParams)
      .then(response => {
        dispatch(getAdminTimeTrackingSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(timeTrackingHasErrored(true, error.response.data, constants.GET_ADMIN_TIME_TRACKING_ENTRIES))
        return []
      })
  }
}

export const getAdminTimeTrackingSuccess = (timeTrackingEntries: ITimeTracking[]): GetAdminTimeTrackingEntriesSuccess => {
  return {
    type: constants.GET_ADMIN_TIME_TRACKING_ENTRIES_SUCCESS,
    timeTrackingEntries
  }
}

export const updateAdminTimeTracking: ActionCreator<TimeTrackingThunkResult<ITimeTracking>>
  = (entry: ITimeTracking): TimeTrackingThunkResult<ITimeTracking>  => {
  return async (dispatch: TimeTrackingThunkDispatchType) => {
    return TimeTrackingApi.updateAdminTimeTrackingEntry(entry)
      .then(response => {
        dispatch(updateTimeTrackingSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(timeTrackingHasErrored(true, error.response.data, constants.UPDATE_ADMIN_TIME_TRACKING_ENTRY))
        return {} as ITimeTracking
      })
  }
}

export const updateAdminTimeTrackingSuccess = (timeTrackingEntry: ITimeTracking): UpdateAdminTimeTrackingEntrySuccess => {
  toastMsg(`Updated tracked time for ${timeTrackingEntry.activity ? timeTrackingEntry.activity.name : timeTrackingEntry.direct_cost.offer_position} successfully.`, constants.UPDATE_TIME_TRACKING_ENTRY_SUCCESS, 'success')
  return {
    type: constants.UPDATE_ADMIN_TIME_TRACKING_ENTRY_SUCCESS,
    timeTrackingEntry
  }
}

export const createAdminTimeTracking: ActionCreator<TimeTrackingThunkResult<ITimeTracking>>
  = (entry: ITimeTracking, employeeId: string | number): TimeTrackingThunkResult<ITimeTracking>  => {
  return async (dispatch: TimeTrackingThunkDispatchType) => {
    return TimeTrackingApi.createAdminTimeTrackingEntry(entry, employeeId)
      .then(response => {
        dispatch(createAdminTimeTrackingSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(timeTrackingHasErrored(true, error.response.data, constants.CREATE_ADMIN_TIME_TRACKING_ENTRY))
        return {} as ITimeTracking
      })
    }
  }

  export const createAdminTimeTrackingSuccess = (timeTrackingEntry: ITimeTracking): CreateAdminTimeTrackingEntrySuccess => {
    toastMsg(`Created tracked time entry successfully.`, constants.CREATE_ADMIN_TIME_TRACKING_ENTRY_SUCCESS, 'success')
    return {
      type: constants.CREATE_ADMIN_TIME_TRACKING_ENTRY_SUCCESS,
      timeTrackingEntry
    }
  }


export const getTimeTrackingInvolvedTasks: ActionCreator<TimeTrackingThunkResult<ITimeTrackingInvolvedTasks[]>>
= (queryParams: Record<string, string>): TimeTrackingThunkResult<ITimeTrackingInvolvedTasks[]> => {
  return async (dispatch: TimeTrackingThunkDispatchType) => {
    return TimeTrackingApi.getTimeTrackingInvolvedTasks(queryParams)
      .then(response => {
        dispatch(getTimeTrackingInvolvedTasksSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(getTimeTrackingInvolvedTasksHasErrored(true, error.response.data, constants.GET_TIME_TRACKING_INVOLVED_TASKS))
        return {} as ITimeTrackingInvolvedTasks[]
      })
  }
}


export const getTimeTrackingInvolvedTasksSuccess = (timeTrackingInvolvedTasks: ITimeTrackingInvolvedTasks[]): GetTimeTrackingInvolvedTasksSuccess => {
  return {
    type: constants.GET_TIME_TRACKING_INVOLVED_TASKS_SUCCESS,
    timeTrackingInvolvedTasks
  }
}


export const deleteTimeTracking: ActionCreator<TimeTrackingThunkResult<ITimeTracking>>
  = (entry: ITimeTracking): TimeTrackingThunkResult<ITimeTracking>  => {
  return async (dispatch: TimeTrackingThunkDispatchType) => {
    return TimeTrackingApi.deleteTimeTrackingEntry(entry)
      .then(response => {
        dispatch(deleteTimeTrackingSuccess(entry))
        return response
      })
      .catch(error => {
        dispatch(timeTrackingHasErrored(true, error.response.data, constants.DELETE_TIME_TRACKING_ENTRY))
        return {} as ITimeTracking
      })
  }
}

export const deleteTimeTrackingSuccess = (timeTrackingEntry: ITimeTracking): DeleteTimeTrackingEntrySuccess => {
  toastMsg(`Deleted tracked time successfully.`, constants.DELETE_TIME_TRACKING_ENTRY_SUCCESS, 'success')
  return {
    type: constants.DELETE_TIME_TRACKING_ENTRY_SUCCESS,
    timeTrackingEntry
  }
}

export const getTimeTrackingInvolvedTasksHasErrored = (hasErrored: boolean, error: Error, action?: string): GetTimeTrackingInvolvedTasksHasErrored => {
  if (action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.GET_TIME_TRACKING_INVOLVED_TASKS_HAS_ERRORED,
    hasErrored: hasErrored
  }
}

export const filterTimeTrackingInvolvedTasks = (filters: ITimeTrackingInvolvedTaskFilter): FilterTimeTrackingInvolvedTasks => {
  return {
    type: constants.FILTER_TIME_TRACKING_INVOLVED_TASKS,
    filters
  }
}

//** CSV

export const downloadEmployeeTimesheetCSV: ActionCreator<TimeTrackingThunkResult<void | Blob | AxiosResponse<Blob>>>
  = (queryParams: Record<string, string | boolean | number>): TimeTrackingThunkResult<void | Blob | AxiosResponse<Blob>> => {
  return async () => {
    return TimeTrackingApi.exportEmployeeTimesheetCSV(queryParams).then((response) => {
      toastMsg("CSV successfully exported", "CSV EXPORT", 'success')
      return response
    }).catch((error: Error) => toastMsg(error.message, "CSV EXPORT", 'error'))
  }
}

//** Involved Task Report */

export const getPaginatedProjectTimeTrackingInvolvedTasks: ActionCreator<TimeTrackingThunkResult<IPagination<ITimeTrackingInvolvedTasks[]>>>
  = (queryParams: Record<string, string>): TimeTrackingThunkResult<IPagination<ITimeTrackingInvolvedTasks[]>> => {
    return async (dispatch: TimeTrackingThunkDispatchType) => {
      return TimeTrackingApi.getPaginatedTimeTrackingInvolvedTasks(queryParams)
        .then(response => {
          dispatch(getPaginatedProjectTimeTrackingInvolvedTasksSuccess(response as IPagination<ITimeTrackingInvolvedTasks[]>))
          return response
        })
        .catch(error => {
          dispatch(getTimeTrackingInvolvedTasksHasErrored(true, error.response.data, constants.GET_PAGINATED_TIME_TRACKING_INVOLVED_TASKS))
          return {} as IPagination<ITimeTrackingInvolvedTasks[]>
        })
    }
  }

export const getPaginatedProjectTimeTrackingInvolvedTasksSuccess = (data: IPagination<ITimeTrackingInvolvedTasks[]>): GetPaginatedProjectTimeTrackingInvolvedTasksSuccess => {
  return {
    type: constants.GET_PAGINATED_TIME_TRACKING_INVOLVED_TASKS_SUCCESS,
    data
  }
}

export const setPaginatedProjectTimeTrackingInvolvedTasksActivePage = (page: number): SetPaginatedProjectTimeTrackingInvolvedTasksActivePage => {
  return {
    type: constants.SET_PAGINATED_TIME_TRACKING_INVOLVED_TASKS_ACTIVE_PAGE,
    page: page
  }
}

export const searchProjectTimeTrackingInvolvedTasks = (queryParams: Record<string, string>): SearchProjectTimeTrackingInvolvedTasks => {
  return {
    type: constants.SEARCH_PROJECT_TIME_TRACKING_INVOLVED_TASKS,
    queryParams
  }
}

export const sortTimeTrackingInvolvedTasksReport = (name: string, direction: string): SortProjectTimeTrackingInvolvedTasksReport => {
  return {
    type: constants.SORT_PROJECT_TIME_TRACKING_INVOLVED_TASKS_REPORT,
    sort: { name: name, direction: direction }
  }
}

/* END */


export const getTimeTrackingChartData: ActionCreator<TimeTrackingThunkResult<ITimeTrackingChart>>
  = (queryParams: Record<string, string>): TimeTrackingThunkResult<ITimeTrackingChart> => {
  return async (dispatch: TimeTrackingThunkDispatchType) => {
    return TimeTrackingApi.getTimeTrackingChartData(queryParams)
      .then(response => {
        dispatch(getTimeTrackingChartSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(timeTrackingHasErrored(true, error.response.data, constants.GET_TIME_TRACKING_CHART_DATA))
        return {} as ITimeTrackingChart
      })
  }
}

export const getTimeTrackingChartSuccess = (timeTrackingChart: ITimeTrackingChart): GetTimeTrackingChartDataSuccess => {
  return {
    type: constants.GET_TIME_TRACKING_CHART_DATA_SUCCESS,
    timeTrackingChart
  }
}

export const getTimeTrackingChartDonutData: ActionCreator<TimeTrackingThunkResult<ITimeTrackingDonutChart>>
  = (queryParams: Record<string, string>): TimeTrackingThunkResult<ITimeTrackingDonutChart> => {
  return async (dispatch: TimeTrackingThunkDispatchType) => {
    return TimeTrackingApi.getTimeTrackingChartDonutData(queryParams)
      .then(response => {
        dispatch(getTimeTrackingChartDonutSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(timeTrackingHasErrored(true, error.response.data, constants.GET_TIME_TRACKING_CHART_DONUT_DATA))
        return {} as ITimeTrackingDonutChart
      })
  }
}

export const getTimeTrackingChartDonutSuccess = (timeTrackingDonutChart: ITimeTrackingDonutChart): GetTimeTrackingChartDataDonutSuccess => {
  return {
    type: constants.GET_TIME_TRACKING_CHART_DATA_DONUT_SUCCESS,
    timeTrackingDonutChart
  }
}

export const getCurrentTasks: ActionCreator<TimeTrackingThunkResult<IProjectCurrentTasks>>
= (queryParams?: Record<string, string>): TimeTrackingThunkResult<IProjectCurrentTasks> => {
  return async (dispatch: TimeTrackingThunkDispatchType) => {
    return TimeTrackingApi.getCurrentTasks(queryParams)
      .then(response => {
        dispatch(getCurrentTasksSuccess(response as IProjectCurrentTasks))
        return response as IProjectCurrentTasks
      })
      .catch(error => {
        dispatch(timeTrackingHasErrored(true, error.response.data, constants.GET_CURRENT_TASKS))
        return {} as IProjectCurrentTasks
      })
  }
}


export const getCurrentTasksSuccess = (data: IProjectCurrentTasks): GetCurrentTasksSuccess => {
  return {
    type: constants.GET_CURRENT_TASKS_SUCCESS,
    data
  }
}

export const getPaginatedCurrentTasks: ActionCreator<TimeTrackingThunkResult<IPagination<IProjectActivity[]>>>
= (queryParams?: Record<string, string> ): TimeTrackingThunkResult<IPagination<IProjectActivity[]>> => {
  return async (dispatch: TimeTrackingThunkDispatchType) => {
    return TimeTrackingApi.getPaginatedCurrentTasks(queryParams)
    .then((response: IPagination<IProjectActivity[]>) => {
      dispatch(getPaginatedCurrentTasksSuccess(response))
      return response
    })
    .catch(error => {
      dispatch(timeTrackingHasErrored(true, error.response.data, constants.GET_PAGINATED_CURRENT_TASKS))
      return {} as IPagination<IProjectActivity[]>
    })
  }
}

export const getPaginatedCurrentTasksSuccess = (data: IPagination<IProjectActivity[]>): GetPaginatedCurrentTasksSuccess => {
  return {
    type: constants.GET_PAGINATED_CURRENT_TASKS_SUCCESS,
    data
  }
}

export const getCurrentDirectCosts: ActionCreator<TimeTrackingThunkResult<IProjectCurrentDirectCosts>>
= (queryParams?: Record<string, string>): TimeTrackingThunkResult<IProjectCurrentDirectCosts> => {
  return async (dispatch: TimeTrackingThunkDispatchType) => {
    return TimeTrackingApi.getCurrentTasks(queryParams)
      .then(response => {
        dispatch(getCurrentDirectCostsSuccess(response as IProjectCurrentDirectCosts))
        return response as IProjectCurrentDirectCosts
      })
      .catch(error => {
        dispatch(timeTrackingHasErrored(true, error.response.data, constants.GET_CURRENT_DIRECT_COSTS))
        return {} as IProjectCurrentDirectCosts
      })
  }
}

export const getCurrentDirectCostsSuccess = (data: IProjectCurrentDirectCosts): GetCurrentDirectCostsSuccess => {
  return {
    type: constants.GET_CURRENT_DIRECT_COSTS_SUCCESS,
    data
  }
}

export const getPaginatedDirectCostsTasks: ActionCreator<TimeTrackingThunkResult<IPagination<IDirectCost[]>>>
= (queryParams?: Record<string, string> ): TimeTrackingThunkResult<IPagination<IDirectCost[]>> => {
  return async (dispatch: TimeTrackingThunkDispatchType) => {
    return TimeTrackingApi.getPaginatedCurrentDirectCosts(queryParams)
    .then(response => {
      dispatch(getPaginatedCurrentDirectCostsSuccess(response as IPagination<IDirectCost[]>))
      return response
    })
    .catch(error => {
      dispatch(timeTrackingHasErrored(true, error.response.data, constants.GET_PAGINATED_CURRENT_DIRECT_COSTS))
      return {} as IPagination<IDirectCost[]>
    })
  }
}

export const getPaginatedCurrentDirectCostsSuccess = (data: IPagination<IDirectCost[]>): GetPaginatedCurrentDirectCostsSuccess => {
  return {
    type: constants.GET_PAGINATED_CURRENT_DIRECT_COSTS_SUCCESS,
    data
  }
}

export const setPaginatedCurrentTasksActivePage = (page: number): SetPaginatedCurrentTasksActivePage => {
  return {
    type: constants.SET_PAGINATED_CURRENT_TASKS_ACTIVE_PAGE,
    page: page
  }
}

export const setCurrentTasksStatusFilter = (status: 'in_progress' | 'completed'): SetCurrentTasksStatusFilter => {
  return {
    type: constants.SET_CURRENT_TASKS_STATUS_FILTER,
    status: status
  }
}


export const selectEmployeeForTimeTracking = (employee: IEmployee | null): SelectEmployeeForTimeTracking => {
  return {
    type: constants.SELECT_EMPLOYEE_FOR_TIME_TRACKING,
    employee
  }
}


export type TimeTrackingActions
  = SetActiveTimetrackingTab
  | TimeTrackingHasErrored
  | GetTimeTrackingEntriesSuccess
  | UpdateTimeTrackingEntrySuccess
  | CreateTimeTrackingEntrySuccess
  | DeleteTimeTrackingEntrySuccess
  | GetTimeTrackingInvolvedTasksSuccess
  | GetTimeTrackingInvolvedTasksHasErrored
  | FilterTimeTrackingInvolvedTasks
  | DeleteTimeTrackingEntrySuccess
  | GetTimeTrackingChartDataSuccess
  | GetTimeTrackingChartDataDonutSuccess
  | GetCurrentTasksSuccess
  | GetPaginatedCurrentTasksSuccess
  | SetPaginatedCurrentTasksActivePage
  | SetCurrentTasksStatusFilter
  | GetAdminTimeTrackingEntriesSuccess
  | CreateAdminTimeTrackingEntrySuccess
  | UpdateAdminTimeTrackingEntrySuccess
  | SelectEmployeeForTimeTracking
  | GetPaginatedProjectTimeTrackingInvolvedTasksSuccess
  | SetPaginatedProjectTimeTrackingInvolvedTasksActivePage
  | SortProjectTimeTrackingInvolvedTasksReport
  | SearchProjectTimeTrackingInvolvedTasks
  | GetCurrentDirectCostsSuccess
  | GetPaginatedCurrentDirectCostsSuccess
